import axios from 'axios';

// Determine API URL based on environment
// Priority: 1. REACT_APP_API_URL env variable, 2. Auto-detect from domain, 3. Default to localhost
let API_BASE_URL = process.env.REACT_APP_API_URL;

// If no environment variable is set, auto-detect based on current origin
if (!API_BASE_URL && typeof window !== 'undefined') {
    const origin = window.location.origin;
    const isProduction = process.env.REACT_APP_ENV === 'production' || 
                         origin.includes('translator.antrohoos.am') || 
                         origin.includes('antrohoos.am');
    
    if (isProduction) {
        // Production domain - use relative path (backend serves frontend from same domain)
        API_BASE_URL = '/api';
        console.log('🌐 Production mode detected - using relative API path');
    } else {
        // Development (localhost:3000, 127.0.0.1, etc.) - use explicit localhost:5000
        API_BASE_URL = 'http://localhost:5000/api';
        console.log('💻 Development mode detected - using localhost:5000');
    }
} else if (!API_BASE_URL) {
    // Fallback for server-side rendering (defaults to development)
    API_BASE_URL = 'http://localhost:5000/api';
    console.log('⚙️  Using default localhost API URL (server-side rendering)');
}

// Create axios instance
console.log('🔧 API Service - API_BASE_URL:', API_BASE_URL);
console.log('🔧 API Service - process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('🔧 API Service - Window origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');

// Use the determined API URL
const finalApiUrl = API_BASE_URL;
console.log('🔧 API Service - Final API URL:', finalApiUrl);

const api = axios.create({
    baseURL: finalApiUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: 30000, // 30 seconds timeout
    withCredentials: false, // Don't send credentials for CORS
    mode: 'cors' // Explicitly set CORS mode
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        console.log('🔑 API Interceptor - Token exists:', !!token);
        console.log('🔑 API Interceptor - Token value:', token ? token.substring(0, 20) + '...' : 'null');
        console.log('🔑 API Interceptor - Request URL:', config.url);
        console.log('🔑 API Interceptor - Full URL:', config.baseURL + config.url);
        console.log('🔑 API Interceptor - Method:', config.method);
        console.log('🔑 API Interceptor - Headers:', JSON.stringify(config.headers, null, 2));
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('✅ API Interceptor - Authorization header set');
        } else {
            console.log('ℹ️ API Interceptor - No token (this is normal for login requests)');
        }
        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Handle token expiration and network errors
api.interceptors.response.use(
    (response) => {
        console.log('✅ API Response:', response.status, response.config.url);
        return response;
    },
    (error) => {
        console.error('❌ API Error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error name:', error.name);
        console.error('API Base URL:', API_BASE_URL);
        console.error('Request URL:', error.config?.url);
        console.error('Full URL:', error.config?.baseURL + error.config?.url);
        console.error('Request method:', error.config?.method);
        console.error('Request headers:', error.config?.headers);
        console.error('Error response:', error.response);
        console.error('Error response status:', error.response?.status);
        console.error('Error response data:', error.response?.data);
        console.error('Error stack:', error.stack);
        
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
            console.error('❌ Network Error detected!');
            console.error('This usually means:');
            console.error('1. Server is not running');
            console.error('2. CORS issue');
            console.error('3. Firewall blocking connection');
            console.error('4. Browser cannot reach server');
        }
        
        // Only redirect to login if response clearly indicates auth failure (token invalid/expired)
        // Don't redirect for 401/403 from WAF/proxy or permission denied — show error instead
        const status = error.response?.status;
        const errMsg = (error.response?.data?.error || '').toLowerCase();
        const isAuthFailure = (status === 401 && (errMsg.includes('token') || errMsg.includes('auth'))) ||
            (status === 403 && (
                errMsg.includes('invalid') || errMsg.includes('expired') || errMsg.includes('token') ||
                errMsg.includes('user not found') || errMsg.includes('inactive')
            ));
        const isPermissionDenied = status === 403 && !isAuthFailure;
        if (isPermissionDenied) {
            console.warn('⚠️ 403 Permission denied (not redirecting):', errMsg);
        } else if (status === 401 && !errMsg.includes('token') && !errMsg.includes('auth')) {
            console.warn('⚠️ 401 without auth message (not redirecting):', errMsg);
        } else if (isAuthFailure && error.config?.url !== '/auth/login' && localStorage.getItem('token')) {
            console.log('⚠️ Authentication failed, clearing auth and redirecting to login');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (username, password) => 
        api.post('/auth/login', { username, password }),
    
    getMe: () => 
        api.get('/auth/me')
};

// Projects API
export const projectsAPI = {
    getAll: () => 
        api.get('/projects'),
    
    getById: (id) => 
        api.get(`/projects/${id}`),
    
    create: (data) => 
        api.post('/projects', data),
    
    update: (id, data) => 
        api.put(`/projects/${id}`, data),
    
    delete: (id) => 
        api.delete(`/projects/${id}`)
};

// Translations API
export const translationsAPI = {
    getByProject: (projectId) => 
        api.get(`/translations/project/${projectId}`),
    
    updateEntry: (entryId, field, value) => 
        api.put(`/translations/entry/${entryId}`, { field, value }),
    
    createEntry: (projectId, data) =>
        api.post(`/translations/project/${projectId}/entry`, data),
    
    sendToWorkflow: (entryId, to) =>
        api.post(`/translations/entry/${entryId}/send`, { to }),
    
    addComment: (entryId, fieldName, commentText, isSuggestion) =>
        api.post(`/translations/entry/${entryId}/comment`, { fieldName, commentText, isSuggestion }),
    
    getComments: (entryId) =>
        api.get(`/translations/entry/${entryId}/comments`),
    
    resolveComment: (commentId) =>
        api.post(`/translations/comment/${commentId}/resolve`),
    
    confirmEntry: (entryId, confirmed) => 
        api.post(`/translations/entry/${entryId}/confirm`, { confirmed }),
    
    exportProject: (projectId, entryIds = null) => {
        const params = entryIds && entryIds.length > 0 ? { entryIds: entryIds.join(',') } : {};
        return api.get(`/translations/project/${projectId}/export`, { 
            params,
            responseType: 'blob' 
        });
    },
    
    deleteEntry: (entryId) =>
        api.delete(`/translations/entry/${entryId}`)
};

export const dictionaryAPI = {
    getTerms: (q) =>
        api.get('/dictionary', { params: q ? { q } : {} })
};

// Upload API
export const uploadAPI = {
    uploadExcel: (file, name, description) => {
        const formData = new FormData();
        formData.append('file', file);
        if (name) formData.append('name', name);
        if (description) formData.append('description', description);
        
        return api.post('/upload/excel', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }
};

// Admin API
export const adminAPI = {
    getStats: () => 
        api.get('/admin/stats'),
    
    getUsers: () => 
        api.get('/admin/users'),
    
    createUser: (data) => 
        api.post('/admin/users', data),
    
    updateUser: (userId, data) => 
        api.put(`/admin/users/${userId}`, data),
    
    getActivityLogs: (limit = 50, offset = 0) => 
        api.get('/admin/activity', { params: { limit, offset } }),
    
    getLanguageConfig: () =>
        api.get('/admin/language-config'),
    
    updateLanguageConfig: (data) =>
        api.put('/admin/language-config', data),
    
    getPermissions: () =>
        api.get('/admin/permissions'),
    
    updatePermissions: (role, permissions) =>
        api.put(`/admin/permissions/${role}`, { permissions })
};

// Notifications API
export const notificationsAPI = {
    getAll: () => 
        api.get('/notifications'),
    
    markAsRead: (notificationId) => 
        api.put(`/notifications/${notificationId}/read`),
    
    markAllAsRead: () => 
        api.put('/notifications/read-all')
};

// Projects API - assignments
export const projectsAssignmentsAPI = {
    getAssignments: (projectId) => 
        api.get(`/projects/${projectId}/assignments`),
    
    assignUser: (projectId, userId, role) => 
        api.post(`/projects/${projectId}/assignments`, { userId, role }),
    
    removeAssignment: (projectId, userId) => 
        api.delete(`/projects/${projectId}/assignments/${userId}`)
};

export default api;

